import { AppRoutesNS } from 'src/app/app-routes.namespace'
import { combineLatest, Observable, of, timer } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap
} from 'rxjs/operators'
import { authActions } from 'src/app/core/auth/auth.actions'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { AppState } from 'src/app/core/core.state'
import { configurationOptionSelectors } from 'src/app/features/configuration-option/configuration-option.selectors'
import { Member, MemberStatus } from 'src/app/features/member/member'
import { RolesType } from 'src/app/features/role/role'
import { userRoleSelectors } from 'src/app/features/role/user-role/user-role.selectors'
import { MenuItem } from 'src/app/menu/menu.model'
import { menuSelectors } from 'src/app/menu/menu.selectors'
import { environment } from 'src/environments/environment'
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core'
import { Store } from '@ngrx/store'
import { ConfigurationService } from '../../shared/services/configuration.service'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import {
  ActivatedRoute,
  NavigationEnd,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router
} from '@angular/router'
import { TicketingService } from 'src/app/features/shop/ticketing/ticketing.service'
import { menuActions } from 'src/app/menu/menu.actions'
import { BreadCrumbActions } from 'src/app/shared/components/breadcrumb/breadcrumb.actions'
import { Branding, ModuleConfigs } from 'src/environments/environment-model'
import { NavItem } from 'src/app/shared/interfaces'
import {
  ConfigurationOption,
  HomePageConfigWebOption
} from 'src/app/features/configuration-option/configuration-option'
import {
  DiscoveryAreaConfig,
  DiscoveryAreaConfigType
} from 'src/app/shared/components/discovery-area/discovery-area.types'
import { ISO639LanguageCodes } from 'src/app/shared/utils/languageCodes'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { ConfigOptionsKeys } from 'src/app/shared/constants'

interface HiddenMenuRule {
  link: string
  hideOnMobileApp?: boolean
  navBarName?: string
}

@UntilDestroy()
@Component({
  selector: 'primary-navbar',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PrimaryNavbarComponent implements OnInit, OnDestroy {
  data: any[] = []
  public appRoutesNS = AppRoutesNS
  public menuItems$: Observable<MenuItem[]>
  public showMobileMenu: boolean = false
  public primaryLogoUrl$: Observable<string>
  public staticAssetsUrl: string
  public brandingConfig: Branding
  public moduleConfigs: ModuleConfigs
  public member$: Observable<Member>
  public hasEmployeeOrSupervisorRole$: Observable<boolean>
  public additionalMenuItems = environment.additionalMenuItems
  public showUserRoleSwitcher = false
  public routerIsLoadingData$: Observable<boolean>
  public showPendingSaleBlock$: Observable<boolean>
  public showCartButton$: Observable<boolean>

  private hiddenMenuRules: HiddenMenuRule[] = [
    { link: AppRoutesNS.MyWork.MY_WORK, hideOnMobileApp: true },
    { link: AppRoutesNS.Competitions.COMPETITIONS, hideOnMobileApp: true },
    {
      link: AppRoutesNS.Upcoming.UPCOMING_BOOKINGS,
      hideOnMobileApp: true,
      navBarName: 'mybooking-organisation'
    }
  ]
  constructor(
    private store: Store<AppState>,
    public configurationService: ConfigurationService,
    public router: Router,
    private activatedRoute: ActivatedRoute,
    public ticketingservice: TicketingService
  ) {}

  get logoRedirectUrl(): string {
    if (!this.moduleConfigs.bookingModule?.isActive) {
      return this.appRoutesNS.Members.PROFILE_MYSHOPPING
    }

    return (
      this.moduleConfigs.authModule?.afterLoginSuccessRedirect ||
      this.appRoutesNS.Booking.ACTIVITIES
    )
  }

  private shouldHideMenuItem(
    menuItem: { link: string; navBarName?: string },
    isMobileApp: boolean
  ): boolean {
    return this.hiddenMenuRules.some((rule) => {
      const matchLink = rule.link === menuItem.link
      const matchNavBar = rule.navBarName
        ? rule.navBarName === menuItem.navBarName
        : true
      const matchMobile = rule.hideOnMobileApp ? isMobileApp : true
      return matchLink && matchNavBar && matchMobile
    })
  }

  private isMenuItemAllowedOnMobile(
    menuItem: { link: string },
    isMobileApp: boolean
  ): boolean {
    return this.configurationService.isRouteAllowedOnMobileAppForState(
      menuItem.link,
      isMobileApp
    )
  }

  ngOnInit() {
    this.member$ = this.store
      .select(authSelectors.selectMember)
      .pipe(shareReplay(1))
    this.moduleConfigs = environment.moduleConfigs

    this.hasEmployeeOrSupervisorRole$ = this.store
      .select(userRoleSelectors.selectUserHasRoles, [
        RolesType.EMPLOYEE,
        RolesType.SUPERVISOR
      ])
      .pipe(shareReplay(1))

    this.routerIsLoadingData$ = this.router.events.pipe(
      filter(
        (event) =>
          event instanceof RouteConfigLoadStart ||
          event instanceof RouteConfigLoadEnd
      ),
      map((event) => event instanceof RouteConfigLoadStart),
      switchMap((isLoading) =>
        isLoading ? timer(200).pipe(map(() => true)) : of(false)
      )
    )

    this.showPendingSaleBlock$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null),
      map(() => !this.currentRouteHidesPendingSaleBlock()),
      distinctUntilChanged()
    )

    this.showCartButton$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null),
      map(() => !this.currentRouteHidesCartButton()),
      distinctUntilChanged()
    )

    this.menuItems$ = combineLatest([
      this.store.select(menuSelectors.selectMenuItems),
      this.hasEmployeeOrSupervisorRole$,
      this.member$,
      this.configurationService.isMobileApp$,
      this.store.select(configurationOptionSelectors.selectAll),
      this.store.select(settingSelectors.selectById, 'language')
    ]).pipe(
      map(
        ([
          menuItems,
          hasEmployeeOrSupervisorRole,
          member,
          isMobileApp,
          configurationOptions,
          selectedLanguage
        ]) => {
          const filteredMenuItems = this.filterMenuItems(
            menuItems,
            hasEmployeeOrSupervisorRole,
            member,
            isMobileApp,
            configurationOptions
          )
          return this.mapDynamicTitles(
            filteredMenuItems,
            configurationOptions,
            member?.language ?? selectedLanguage ?? ISO639LanguageCodes.English
          )
        }
      )
    )

    this.primaryLogoUrl$ = this.store
      .select(
        configurationOptionSelectors.selectByKey,
        'primary_logo_image_path'
      )
      .pipe(
        filter((x) => Boolean(x)),
        map((configOption) =>
          configOption?.value.length
            ? `${environment.staticAssetBaseUrl}${configOption?.value}`
            : ''
        )
      )

    this.brandingConfig = environment.branding
  }

  private currentRouteHidesPendingSaleBlock(): boolean {
    let route = this.activatedRoute
    let hidePendingSaleBlock =
      route.snapshot.data.layout?.hidePendingSaleBlock === true

    while (route.firstChild) {
      route = route.firstChild
      hidePendingSaleBlock =
        hidePendingSaleBlock ||
        route.snapshot.data.layout?.hidePendingSaleBlock === true
    }

    return hidePendingSaleBlock
  }

  private currentRouteHidesCartButton(): boolean {
    let route = this.activatedRoute
    let hideCartButton = route.snapshot.data.layout?.hideCartButton === true

    while (route.firstChild) {
      route = route.firstChild
      hideCartButton =
        hideCartButton || route.snapshot.data.layout?.hideCartButton === true
    }

    return hideCartButton
  }

  private filterMenuItems(
    menuItems: MenuItem[],
    hasEmployeeOrSupervisorRole: boolean,
    member: Member | null,
    isMobileApp: boolean,
    configurationOptions: ConfigurationOption[]
  ): MenuItem[] {
    const discoveryAreaConfigOption = configurationOptions.find(
      (c) => c.key == 'discovery_area_config'
    )
    let discoveryAreaConfig: DiscoveryAreaConfig[] = null
    if (discoveryAreaConfigOption?.value) {
      discoveryAreaConfig = JSON.parse(discoveryAreaConfigOption.value)
    }

    const activitiesDiscoveryArea: DiscoveryAreaConfig =
      discoveryAreaConfig?.find(
        (x) => x.type == DiscoveryAreaConfigType.ACTIVITY_DISCOVERY
      )
    const rentDiscoveryArea: DiscoveryAreaConfig = discoveryAreaConfig?.find(
      (x) => x.type == DiscoveryAreaConfigType.RENTAL_DISCOVERY
    )

    const overviewDiscoveryArea = discoveryAreaConfig?.find(
      (x) => x.type == DiscoveryAreaConfigType.OVERVIEW_DISCOVERY
    )
    const homePageConfigOption = configurationOptions.find(
      (c) => c.key == ConfigOptionsKeys.HOME_PAGE_CONFIG
    )

    const filterConditions: { [key: string]: (item?: MenuItem) => boolean } = {
      [AppRoutesNS.Home.HOME]: () => Boolean(homePageConfigOption?.value),
      [AppRoutesNS.Booking.ACTIVITIES]: () => Boolean(activitiesDiscoveryArea),
      [AppRoutesNS.Booking.RENT]: () => Boolean(rentDiscoveryArea),
      [AppRoutesNS.Overview.OVERVIEW]: () => Boolean(overviewDiscoveryArea),
      [AppRoutesNS.Shop.SHOP]: () => this.moduleConfigs.shopModule.isActive,
      [AppRoutesNS.MyWork.MY_WORK]: () =>
        (this.moduleConfigs.employeeModule.isActive ||
          this.moduleConfigs.supervisorModule.isActive) &&
        hasEmployeeOrSupervisorRole &&
        member &&
        member?.status !== MemberStatus.GUEST,
      [AppRoutesNS.Competitions.COMPETITIONS]: () =>
        (this.moduleConfigs.competitionModule.isActive ||
          this.moduleConfigs.competitionModule.showOnlyCompetitionStanding) &&
        member &&
        member?.status !== MemberStatus.GUEST,
      //pending to be done for v12
      // [AppRoutesNS.Dashboard.DASHBOARD]: (menuItem) =>
      //   menuItem?.navBarName === 'mybooking-organisation'
      //     ? this.moduleConfigs.dashboardModule.isActive &&
      //       this.moduleConfigs.dashboardModule.showMybookingOrgTab &&
      //       member &&
      //       member?.status !== MemberStatus.GUEST
      //     : this.moduleConfigs.dashboardModule.isActive &&
      //       member &&
      //       member?.status !== MemberStatus.GUEST,
      [AppRoutesNS.Upcoming.UPCOMING_BOOKINGS]: () =>
        this.moduleConfigs.dashboardModule.isActive &&
        member &&
        member?.status !== MemberStatus.GUEST
    }

    return menuItems.filter((menuItem) => {
      if (this.shouldHideMenuItem(menuItem, isMobileApp)) {
        return false
      }
      if (!this.isMenuItemAllowedOnMobile(menuItem, isMobileApp)) {
        return false
      }
      return filterConditions[menuItem.link]?.(menuItem) ?? false
    })
  }

  private mapDynamicTitles(
    menuItems: MenuItem[],
    configurationOptions: ConfigurationOption[],
    selectedLanguage: ISO639LanguageCodes
  ): MenuItem[] {
    // start new discovery area based logic logic
    const discoveryAreaConfigOption = configurationOptions.find(
      (c) => c.key == ConfigOptionsKeys.DISCOVERY_AREA_CONFIG
    )
    let discoveryAreaConfig: DiscoveryAreaConfig[] = null
    if (discoveryAreaConfigOption?.value) {
      discoveryAreaConfig = JSON.parse(discoveryAreaConfigOption.value)
    }

    const activitiesDiscoveryArea: DiscoveryAreaConfig =
      discoveryAreaConfig?.find(
        (x) => x.type == DiscoveryAreaConfigType.ACTIVITY_DISCOVERY
      )
    const rentDiscoveryArea: DiscoveryAreaConfig = discoveryAreaConfig?.find(
      (x) => x.type == DiscoveryAreaConfigType.RENTAL_DISCOVERY
    )

    const overviewDiscoveryArea: DiscoveryAreaConfig =
      discoveryAreaConfig?.find(
        (x) => x.type == DiscoveryAreaConfigType.OVERVIEW_DISCOVERY
      )
    const homePageConfigOption = configurationOptions.find(
      (c) => c.key == ConfigOptionsKeys.HOME_PAGE_CONFIG
    )
    const homePageConfig: HomePageConfigWebOption | null =
      homePageConfigOption?.value
        ? JSON.parse(homePageConfigOption.value)
        : null
    // end new discovery area based logic logic
    return menuItems.map((menuItem) => {
      switch (menuItem.link) {
        case AppRoutesNS.Home.HOME:
          return {
            ...menuItem,
            translateString:
              homePageConfig?.title?.[
                selectedLanguage as keyof HomePageConfigWebOption['title']
              ] || menuItem.translateString
          }
        case AppRoutesNS.Booking.ACTIVITIES:
          return {
            ...menuItem,
            translateString:
              activitiesDiscoveryArea?.['title_' + selectedLanguage] ||
              menuItem.translateString
          }
        case AppRoutesNS.Booking.RENT:
          return {
            ...menuItem,
            translateString:
              rentDiscoveryArea?.['title_' + selectedLanguage] ||
              menuItem.translateString
          }
        case AppRoutesNS.Overview.OVERVIEW:
          return {
            ...menuItem,
            translateString:
              overviewDiscoveryArea?.['title_' + selectedLanguage] ||
              menuItem.translateString
          }
        default:
          break
      }
      return menuItem
    })
  }

  public onClickAction(action: string) {
    switch (action) {
      case 'logout':
        return this.store.dispatch(authActions.logout({}))

      default:
        console.log('No action mathed')
        break
    }
  }

  public onClickScan() {
    this.configurationService.sendEventToAPP({
      eventType: 'OPEN_CAMERA'
    })
  }

  public setShowUserRoleSwithcher(value: boolean) {
    this.showUserRoleSwitcher = value
  }
  public goLogin() {
    this.router.navigateByUrl(AppRoutesNS.Pages.LOGIN)
  }
  public ticketingReset() {
    this.store.dispatch(menuActions.setTicketingControl({ event: true }))
  }
  onLinkClick(item: NavItem, event: MouseEvent): void {
    const currentUrl = this.router.url.split('?')[0]
    const targetUrl = item.link.split('?')[0]
    if (currentUrl === targetUrl) {
      event.preventDefault()
      this.reloadComponent(item)
    } else {
      this.store.dispatch(
        BreadCrumbActions.addPageToHistory({
          pageName: item.translateString,
          url: item.link,
          showBackArrow: false,
          titleName: item.translateString
        })
      )
      this.router.navigate([item.link], { queryParams: item.queryParams })
    }
  }
  reloadComponent(item: NavItem): void {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([item.link], { queryParams: item.queryParams })
    })
  }

  ngOnDestroy(): void {}
  trackByMenuItem(index: number, item: MenuItem): string {
    return item.link || index.toString()
  }

  public additionalMenuItemsFiltered() {
    return this.configurationService.isMobileApp$.pipe(
      map((isMobileApp) =>
        this.additionalMenuItems.filter((item) =>
          this.isMenuItemAllowedOnMobile(item, isMobileApp)
        )
      ),
      untilDestroyed(this)
    )
  }
}
